import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, ChevronDown, X } from "lucide-react";
import { MEDIA } from "@/lib/media";
import { SEASONS, useActiveSeasons } from "@/lib/seasons";
import { useCollectionCounts, isLiveLink } from "@/lib/collections";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const seasonalFor = (key) => SEASONS.map((s) => ({ slug: s.slug, label: `${s.emoji} ${s.label}`, to: `/shop?collection=${key}&seasonal=${s.slug}` }));
const CLOTHING_GROUPS = [
  { label: "Unisex", key: "unisex" },
  { label: "Kids", key: "kids" },
].map((g) => ({ ...g, to: `/shop?collection=${g.key}`, childrenLabel: "Seasonal", children: seasonalFor(g.key) }));

const MENU = [
  {
    label: "Shop", to: "/shop", items: [
      { label: "Shop All", to: "/shop" },
      { label: "New Arrivals", to: "/shop?is_new=true" },
      { label: "Best Sellers", to: "/shop?best_seller=true" },
      { label: "Books", to: "/shop?collection=books" },
      { label: "Stickers & Transfers", to: "/shop?collection=stickers-transfers" },
      { label: "Clothing", to: "/shop?collection=unisex" },
      { label: "Accessories", to: "/shop?collection=accessories" },
      { label: "Dog Collection", to: "/shop?collection=dogs" },
      { label: "Gifts & Bundles", to: "/shop?collection=gifts" },
      { label: "🎁 Build a Bundle", to: "/bundle-builder" },
    ],
  },
  {
    label: "Books", to: "/shop?collection=books", items: [
      { label: "The Great Biscuit Bonanza", to: "/product/chewie-solo-the-great-biscuit-bonanza" },
      { label: "Future Adventures", to: "/adventures" },
      { label: "Book Bundles", to: "/shop?collection=book-bundles" },
    ],
  },
  {
    label: "Clothing", to: "/shop?collection=unisex", columns: true, items: CLOTHING_GROUPS,
  },
  {
    label: "Dog Shop", to: "/shop?collection=dogs", items: [
      { label: "Dog Clothing", to: "/shop?collection=dog-shirts" },
      { label: "Bandanas", to: "/shop?collection=bandanas" },
      { label: "Accessories", to: "/shop?collection=dog-accessories" },
    ],
  },
  { label: "Meet Chewie & Solo", to: "/meet" },
  { label: "Our Story", to: "/our-story" },
  { label: "Adventures", to: "/adventures" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { cartCount, setCartOpen, wishlist } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const activeSeasons = useActiveSeasons();
  const inSeason = (children) => (children || []).filter((c) => !c.slug || activeSeasons.includes(c.slug));
  const counts = useCollectionCounts();
  const menu = MENU.map((m) => (m.items ? { ...m, items: m.items.filter((it) => isLiveLink(it.to, counts)) } : m))
    .filter((m) => isLiveLink(m.to, counts) || (m.items && m.items.length > 0))
    .map((m) => (m.items && !isLiveLink(m.to, counts) ? { ...m, to: m.items[0].to } : m));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setQ(""); }
  };

  return (
    <header className={`sticky top-0 z-50 bg-[#FFF9EE]/90 backdrop-blur-md border-b border-[#19324A]/10 transition-shadow ${scrolled ? "shadow-md" : ""}`}
      data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20 gap-3">
        {/* Mobile hamburger */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="p-2 -ml-2 text-[#19324A]" aria-label="Open menu" data-testid="mobile-menu-trigger">
                <Menu className="w-7 h-7" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[86vw] max-w-sm bg-[#FFF9EE] p-0 overflow-y-auto">
              <SheetTitle className="sr-only">Main menu</SheetTitle>
              <div className="p-5 bg-[#4CC9F0]/20 flex items-center gap-3">
                <img src={MEDIA.logoArc} alt="The Happy Bulldog Co." className="h-16 w-auto object-contain" />
              </div>
              <nav className="p-4">
                <Accordion type="single" collapsible className="w-full">
                  {menu.map((m, i) => m.items ? (
                    <AccordionItem key={m.label} value={`m-${i}`} className="border-b border-[#19324A]/10">
                      <AccordionTrigger className="font-display font-semibold text-[#19324A] hover:no-underline py-3">{m.label}</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col gap-1 pb-2">
                          {m.items.map((it) => (
                            <React.Fragment key={it.label}>
                              <Link to={it.to} onClick={() => setMobileOpen(false)}
                                className="py-2 px-3 rounded-xl text-[#19324A]/80 hover:bg-[#FFD93D]/40 font-medium"
                                data-testid={`mnav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{it.label}</Link>
                              {inSeason(it.children).length > 0 && (
                                <div className="px-3 pb-2">
                                  {it.childrenLabel && <p className="text-[10px] uppercase tracking-wider font-bold text-[#FF6B9D] mb-1.5">{it.childrenLabel}</p>}
                                  <div className="flex flex-wrap gap-1.5" data-testid={`mnav-seasonal-list${it.key ? `-${it.key}` : ""}`}>
                                    {inSeason(it.children).map((c) => <Link key={c.to} to={c.to} onClick={() => setMobileOpen(false)} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-[#19324A]/80">{c.label}</Link>)}
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ) : (
                    <Link key={m.label} to={m.to} onClick={() => setMobileOpen(false)}
                      className="block py-3 font-display font-semibold text-[#19324A] border-b border-[#19324A]/10"
                      data-testid={`mnav-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{m.label}</Link>
                  ))}
                </Accordion>
                <div className="grid grid-cols-2 gap-2 mt-4" data-testid="mnav-quick-links">
                  {[["Pup of the Month", "/pup-of-the-month"], ["Pawparazzi Red Carpet", "/pawparazzi-red-carpet"], ["Size Guide", "/size-guide"], ["FAQ", "/faq"], ["Gift Cards", "/gift-cards"], [user ? "My Account" : "Sign in", user ? "/account" : "/login"], ["Wishlist", "/wishlist"]].map(([label, to]) => (
                    <Link key={to} to={to} onClick={() => setMobileOpen(false)} className="px-3 py-2.5 rounded-xl bg-white text-sm font-semibold text-[#19324A]/80 text-center soft-shadow" data-testid={`mnav-quick-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{label}</Link>
                  ))}
                </div>
                <Link to="/shop" onClick={() => setMobileOpen(false)} className="btn-pink w-full mt-4 !py-3" data-testid="mnav-shop-cta">Shop the collection</Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <img src={MEDIA.logoArc} alt="The Happy Bulldog Co. — A Chewie & Solo Adventure"
            className="h-12 sm:h-16 w-auto object-contain" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {menu.map((m) => (
            <div key={m.label} className="relative group">
              <Link to={m.to}
                className="px-3 py-2 rounded-full font-display font-semibold text-[15px] text-[#19324A] hover:bg-[#FFD93D]/50 inline-flex items-center gap-1 transition-colors">
                {m.label}{m.items && <ChevronDown className="w-4 h-4" />}
              </Link>
              {m.items && (
                <div className={`absolute top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50 ${m.columns ? "left-1/2 -translate-x-1/2" : "left-0"}`}>
                  <div className={`bg-white rounded-2xl soft-shadow border border-[#19324A]/5 p-2 min-w-[230px] ${m.columns ? "grid grid-cols-3 gap-2 w-[600px] xl:w-[690px]" : ""}`}>
                    {m.items.map((it) => (
                      <div key={it.label} className={m.columns ? "min-w-0" : ""}>
                        <Link to={it.to}
                          className="block px-4 py-2.5 rounded-xl text-[#19324A]/85 hover:bg-[#4CC9F0]/15 hover:text-[#19324A] font-medium transition-colors"
                          data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{it.label}</Link>
                        {inSeason(it.children).length > 0 && (
                          <div className="px-4 pb-2">
                            {it.childrenLabel && <p className="text-[10px] uppercase tracking-wider font-bold text-[#FF6B9D] mb-1.5">{it.childrenLabel}</p>}
                            <div className={`flex flex-wrap gap-1.5 ${m.columns ? "" : "max-w-[300px]"}`} data-testid={`nav-seasonal-list${it.key ? `-${it.key}` : ""}`}>
                              {inSeason(it.children).map((c) => <Link key={c.to} to={c.to} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FFF9EE] text-[#19324A]/80 hover:bg-[#FFD93D] transition-colors">{c.label}</Link>)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Icons */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => setSearchOpen((s) => !s)} className="p-2 rounded-full hover:bg-[#FFD93D]/40 text-[#19324A]" aria-label="Search" data-testid="search-toggle">
            <Search className="w-5 h-5" />
          </button>
          <Link to={user ? "/account" : "/login"} className="p-2 rounded-full hover:bg-[#FFD93D]/40 text-[#19324A]" aria-label="Account" data-testid="account-link">
            <User className="w-5 h-5" />
          </Link>
          <Link to="/wishlist" className="relative p-2 rounded-full hover:bg-[#FFD93D]/40 text-[#19324A]" aria-label="Wishlist" data-testid="wishlist-link">
            <Heart className="w-5 h-5" />
            {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF6B9D] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{wishlist.length}</span>}
          </Link>
          <button onClick={() => setCartOpen(true)} className="relative p-2 rounded-full hover:bg-[#FFD93D]/40 text-[#19324A]" aria-label="Shopping bag" data-testid="cart-button">
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF6B9D] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-[#19324A]/10 bg-white" data-testid="search-bar">
          <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2">
            <Search className="w-5 h-5 text-[#19324A]/50" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sniff out something good…"
              className="flex-1 bg-transparent outline-none text-[#19324A] placeholder:text-[#19324A]/40 py-1" data-testid="search-input" />
            <button type="button" onClick={() => setSearchOpen(false)} className="p-1 text-[#19324A]/60"><X className="w-5 h-5" /></button>
          </form>
        </div>
      )}
    </header>
  );
}
